/**
 * Simplify Date Parsing across the app
 * See https://day.js.org/docs/en/display/format for more info about formatting.
 */

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isToday from 'dayjs/plugin/isToday';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(isToday);
dayjs.extend(isBetween);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(utc);
dayjs.extend(timezone);

export const TODAY = dayjs();
export const TOMORROW = dayjs().add(1, 'day');
export const YESTERDAY = dayjs().subtract(1, 'day');

export const parseDate = (date) => {
  return dayjs(date).format('MM/DD/YYYY');
};
export const parseDateDayOfWeek = (date) => {
  return dayjs(date).format('dddd');
};
export const parseDateLong = (date) => {
  return dayjs(date).format('MMMM DD, YYYY');
};
export const parseDateLongWithTime = (date) => {
  return dayjs(date).format('MMMM DD, YYYY hh:mm A');
};
export const parseDateShort = (date) => {
  return dayjs(date).format('MMM. DD');
};
export const parseTime = (time) => {
  return dayjs(time, 'HH:mm:ss').format('hh:mm A');
};
export const parseDateTime = (date, time) => {
  return parseDate(date) + ' ' + parseTime(time);
};
export const isDateValid = (date) => {
  return dayjs(date).isValid();
};
export const isDatePastDated = (date) => {
  return dayjs(date).isBefore(TODAY, 'day');
};
export const parseTimeFromDate = (date) => {
  return dayjs(date).format('hh:mm A');
};
export const isDateToday = (date) => {
  return dayjs(date).isToday();
};
export const isDateCurrentAndPastDated = (date) => {
  return dayjs(date).isBefore(TOMORROW, 'day');
};
export const isDateInvalid = (date) => {
  return !isDateValid(date);
};
export const isDateFutureDated = (date) => {
  return !isDateCurrentAndPastDated(date);
};
export const isEqualOrGreaterThanDateFrom = (dateFrom, dateEnd) => {
  return dayjs(dateEnd).isBefore(dateFrom);
};
export const isEqualOrLessThanDateTo = (dateFrom, dateEnd) => {
  return dayjs(dateFrom).isAfter(dateEnd);
};
export const isWithin90Day = (dateFrom, dateTo) => {
  // 89 as 'dateFrom' value is added to make it 90
  const thirdMonth = dayjs(dateFrom).add(89, 'day');
  return !dayjs(dateTo).isBetween(dayjs(dateFrom), thirdMonth, 'day', '[]');
};
export const isWithin31Day = (dateFrom, dateTo) => {
  // 30 as 'dateFrom' value is added to make it 31
  const thirdMonth = dayjs(dateFrom).add(30, 'day');
  return !dayjs(dateTo).isBetween(dayjs(dateFrom), thirdMonth, 'day', '[]');
};
export const isDateExpired = (date) => {
  return dayjs(date).diff(TODAY) <= 0;
};
export const isDateRangeValid = (dateFrom, dateTo) => {
  if (!dateFrom || !dateTo) {
    return false;
  } else if (isDateInvalid(dateFrom) || isDateInvalid(dateTo)) {
    return false;
  } else if (isDateFutureDated(dateFrom) || isDateFutureDated(dateTo)) {
    return false;
  } else {
    return true;
  }
};
export const isFormatStandard = (date) => {
  return dayjs(date, 'MM/DD/YYYY', true).isValid();
};
export const fileNameDateFormat = (date) => {
  return dayjs(date).format('MMDDYY');
};

export const getMonth = (date) => {
  return dayjs(date).format('MM');
};

export const parseDateMonthText = (date) => {
  return dayjs(date).format('MMM. DD, YYYY');
};
export const parseDateMonthNoPeriod = (date) => {
  return dayjs(date).format('MMM DD, YYYY');
};
export const parseDateMonthNoPeriodNoComma = (date) => {
  return dayjs(date).format('MMM DD YYYY');
};
export const formatDateISO = (date) => {
  return dayjs(date, 'MM/DD/YYYY').format('YYYY-MM-DD');
};
export const formatTimeISO = (time) => {
  return dayjs(time, 'hh:mm A').format('HH:mm:ss');
};
export const convertDateTime = (date, time) => {
  const formattedTime = convertTimeFormat(time);
  return `${formatDateISO(date)}T${formatTimeISO(formattedTime)}`;
};
export const isSameDate = (date1, date2) => {
  return dayjs(date1).isSame(date2);
};
export const isTimeValidStrictUpperCase = (time) => {
  return dayjs(time, 'hh:mm A', true).isValid();
};
export const isTimeValidStrictLowerCase = (time) => {
  return dayjs(time, 'hh:mm a', true).isValid();
};
export const convertTimeFormat = (time) => {
  if (isTimeValidStrictLowerCase(time)) {
    return dayjs(time, 'hh:mm a').format('hh:mm A');
  }
  return time;
};
export const isDateBefore = (dateCompare, dateTo) => {
  return dayjs(dateCompare).isBefore(dateTo);
};
export const isDateBeforeOrEqual = (dateCompare, dateTo) => {
  return dayjs(dateCompare).isSameOrBefore(dateTo);
};
export const isDateAfter = (dateCompare, dateFrom) => {
  return dayjs(dateCompare).isAfter(dateFrom);
};
export const isDateAfterOrEqual = (dateCompare, dateFrom) => {
  return dayjs(dateCompare).isSameOrAfter(dateFrom);
};
export const isDateValidYYYYMMDD = (date) => {
  return dayjs(date, 'YYYY-MM-DD', true).isValid();
};
export const isTimeValid24H = (time) => {
  return dayjs(time, 'HH:mm', true).isValid();
};
export const formatYYYYMMDD = (date) => {
  return dayjs(date, 'YYYY-MM-DD', true).format('MM/DD/YYYY');
};
export const convert24HTo12H = (time) => {
  return dayjs(time, 'HH:mm', true).format('hh:mm A');
};
export const convertToOriginalTimezone = (date, format) => {
  return dayjs.tz(date).format(format);
};
export const convertToMMMDYYYY = (date) => {
  return dayjs(date).format('MMM D, YYYY');
};
export const formatDateWithoutTimezone = (dateString) => {
  const noTimezone = dateString.replace(/([+-]\d{2}:?\d{2}|Z)$/i, '');
  return dayjs(noTimezone);
};
export const convertMMMMD = (date) => {
  return dayjs(date).format('MMMM D');
};
